import { CardContent } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardTitle from "@saleor/components/CardTitle";
import Skeleton from "@saleor/components/Skeleton";
import {
  Timeline,
  TimelineEvent,
  TimelineImage,
  TimelineNote
} from "@saleor/components/Timeline";
import TimelineNoteNationwide from "@saleor/components/Timeline/TimelineNoteNationwide";
import TimelineNoteNationwide3plError from "@saleor/components/Timeline/TimelineNoteNationwide3plError";
import TimelineNoteRemittanceManualAdjust from "@saleor/components/Timeline/TimelineNoteRemittanceManualAdjust";
import {
  OrderDetails_order_events,
  OrderDetails_order_images,
  OrderDetails_order_thirdPartyRider_deliveryCourier_dropOffImageUrl,
  OrderDetails_order_thirdPartyRider_deliveryCourier_pickUpImageUrl,
  OrderDetails_order_thirdPartyRider_deliveryCourier_returnImageUrl
} from "@saleor/motherOrders/types/OrderDetails";
import { isNationwideOrderEvents } from "@saleor/motherOrders/utils/handlers";
import {
  DeliveryTypeEnum,
  OrderEventsEmailsEnum,
  OrderEventsEnum
} from "@saleor/types/globalTypes";
import lodash from "lodash";
import React from "react";
import { IntlShape, useIntl } from "react-intl";

interface OrderHistoryProps {
  events: OrderDetails_order_events[];
  images: OrderDetails_order_images[];
  dropOffImage?: OrderDetails_order_thirdPartyRider_deliveryCourier_dropOffImageUrl;
  pickUpImage?: OrderDetails_order_thirdPartyRider_deliveryCourier_pickUpImageUrl;
  returnImage?: OrderDetails_order_thirdPartyRider_deliveryCourier_returnImageUrl;
  deliveryType: DeliveryTypeEnum;
}

const getEventMessage = (event: OrderDetails_order_events, intl: IntlShape) => {
  switch (event.type) {
    case OrderEventsEnum.DRAFT_ADDED_PRODUCTS:
      return intl.formatMessage({
        defaultMessage: "Products were added to draft order",
        description: "order history message"
      });
    case OrderEventsEnum.DRAFT_CREATED:
      return intl.formatMessage({
        defaultMessage: "Draft order was created",
        description: "order history message"
      });
    case OrderEventsEnum.DRAFT_REMOVED_PRODUCTS:
      return intl.formatMessage({
        defaultMessage: "Products were deleted from draft order",
        description: "order history message"
      });
    case OrderEventsEnum.EMAIL_SENT:
      switch (event.emailType) {
        case OrderEventsEmailsEnum.DIGITAL_LINKS:
          return intl.formatMessage({
            defaultMessage: "Links to the order's digital goods were sent",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.FULFILLMENT_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Fulfillment confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Order confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.PAYMENT_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Payment confirmation was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.SHIPPING_CONFIRMATION:
          return intl.formatMessage({
            defaultMessage: "Shipping details was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.TRACKING_UPDATED:
          return intl.formatMessage({
            defaultMessage: "Shipping tracking number was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_CANCEL:
          return intl.formatMessage({
            defaultMessage: "Order cancel information was sent to customer",
            description: "order history message"
          });
        case OrderEventsEmailsEnum.ORDER_REFUND:
          return intl.formatMessage({
            defaultMessage: "Order refund information was sent to customer",
            description: "order history message"
          });
        default:
          return event.message;
      }
    case OrderEventsEnum.FULFILLMENT_CANCELED:
      return intl.formatMessage({
        defaultMessage: "Fulfillment was cancelled",
        description: "order history message"
      });
    case OrderEventsEnum.INVOICE_REQUESTED:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice was requested by {requestedBy}",
          description: "order history message"
        },
        {
          requestedBy: event.user ? event.user.email : null
        }
      );
    case OrderEventsEnum.INVOICE_GENERATED:
      return intl.formatMessage(
        {
          defaultMessage:
            "Invoice no. {invoiceNumber} was generated by {generatedBy}",
          description: "order history message"
        },
        {
          generatedBy: event.user ? event.user.email : null,
          invoiceNumber: event.invoiceNumber
        }
      );
    case OrderEventsEnum.INVOICE_UPDATED:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice no. {invoiceNumber} was updated",
          description: "order history message"
        },
        {
          invoiceNumber: event.invoiceNumber
        }
      );
    case OrderEventsEnum.INVOICE_SENT:
      return intl.formatMessage(
        {
          defaultMessage: "Invoice was sent to customer by {sentBy}",
          description: "order history message"
        },
        {
          sentBy: event.user ? event.user.email : null
        }
      );
    case OrderEventsEnum.FULFILLMENT_FULFILLED_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Fulfilled {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.FULFILLMENT_RESTOCKED_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Restocked {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.NOTE_ADDED:
      return intl.formatMessage({
        defaultMessage: "Note was added to the order",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_FULLY_PAID:
      return intl.formatMessage({
        defaultMessage: "Order was fully paid",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_PAID:
      return intl.formatMessage({
        defaultMessage: "Marked order as paid",
        description: "order history message"
      });
    case OrderEventsEnum.OVERSOLD_ITEMS:
      return intl.formatMessage(
        {
          defaultMessage: "Oversold {quantity} items",
          description: "order history message"
        },
        {
          quantity: event.quantity
        }
      );
    case OrderEventsEnum.PAYMENT_CAPTURED:
      return intl.formatMessage({
        defaultMessage: "Payment was captured",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_FAILED:
      return intl.formatMessage({
        defaultMessage: "Payment failed",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_VOIDED:
      return intl.formatMessage({
        defaultMessage: "Payment was voided",
        description: "order history message"
      });
    case OrderEventsEnum.PLACED:
      return intl.formatMessage({
        defaultMessage: "Order was placed",
        description: "order history message"
      });
    case OrderEventsEnum.PLACED_FROM_DRAFT:
      return intl.formatMessage({
        defaultMessage: "Order was created from draft",
        description: "order history message"
      });
    case OrderEventsEnum.TRACKING_UPDATED:
      return intl.formatMessage({
        defaultMessage: "Updated fulfillment group's tracking number",
        description: "order history message"
      });
    case OrderEventsEnum.UPDATED_ADDRESS:
      return intl.formatMessage({
        defaultMessage: "Order address was updated",
        description: "order history message"
      });
    case OrderEventsEnum.PAYMENT_AUTHORIZED:
      return intl.formatMessage({
        defaultMessage: "Payment was authorized",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_PICKED_UP_BY_COURIER:
      return intl.formatMessage({
        defaultMessage: "Picked-Up by Courier",
        description: "order history message"
      });
    case OrderEventsEnum.ORDER_MARKED_AS_RECEIVED_BY_COURIER:
      return intl.formatMessage({
        defaultMessage: "Received by Courier",
        description: "order history message"
      });
    default:
      return event.message;
  }
};

const OrderHistory: React.FC<OrderHistoryProps> = props => {
  const {
    events,
    images,
    dropOffImage,
    pickUpImage,
    returnImage,
    deliveryType
  } = props;
  const intl = useIntl();
  const deliveryImage = lodash.last(images);

  const eventsWithMessage = [
    OrderEventsEnum.NOTE_ADDED,
    OrderEventsEnum.STORE_ORDER_PARCEL_NO_SET,
    OrderEventsEnum.STORE_ORDER_SCANNED_BY_RUNNER,
    OrderEventsEnum.STORE_ORDER_SCANNED_BY_OMI_OPS,
    OrderEventsEnum.STORE_ORDER_READY,
    OrderEventsEnum.STORE_ORDER_ACKNOWLEDGED,
    OrderEventsEnum.STORE_ORDER_RECEIVED_AT_FC,
    OrderEventsEnum.STORE_ORDER_CANCELED,
    OrderEventsEnum.STORE_ORDER_COMPLETED,
    OrderEventsEnum.STORE_ORDER_PICK_LINE,
    OrderEventsEnum.STORE_ORDER_ADD_LINE,
    OrderEventsEnum.STORE_ORDER_COLLECTED,
    OrderEventsEnum.STORE_ORDER_DELETE_LINE,
    OrderEventsEnum.ORDER_ASSIGNED_TO_RIDER,
    OrderEventsEnum.ORDER_MARKED_AS_READY_TO_SHIP,
    OrderEventsEnum.ORDER_MARKED_AS_PACKED,
    OrderEventsEnum.ORDER_MARKED_AS_SHIPPED,
    OrderEventsEnum.ORDER_RETURNED_TO_FC,
    OrderEventsEnum.ORDER_MARKED_AS_COMPLETED,
    OrderEventsEnum.ORDER_MARKED_AS_PICKED_UP_BY_COURIER,
    OrderEventsEnum.ORDER_MARKED_AS_RECEIVED_BY_COURIER,
    OrderEventsEnum.ORDER_UPDATED_SHIPPING_LABEL,
    OrderEventsEnum.CANCELED,
    OrderEventsEnum.ORDER_UPDATED_RIDER_DETAILS,
    OrderEventsEnum.ORDER_BOOKED_GRAB_EXPRESS,
    OrderEventsEnum.ORDER_CANCELED_GRAB_EXPRESS,
    OrderEventsEnum.RIDER_ARRIVED,
    OrderEventsEnum.PAYMENT_FAILED_REFUND,
    OrderEventsEnum.PAYMENT_REFUNDED,
    OrderEventsEnum.ORDER_2GO_BOOKING_FAILED,
    OrderEventsEnum.ORDER_2GO_BOOKING_SUCCESS,
    OrderEventsEnum.ORDER_BOOKED_PANDAGO_AUTO,
    OrderEventsEnum.ORDER_PANDAGO_BOOKING_CANCELED,
    OrderEventsEnum.ORDER_PANDAGO_RETURNED_TO_VENDOR,
    OrderEventsEnum.REMITTANCE_MANUAL_OVERRIDE
  ];

  return (
    <Card>
      <CardTitle title="History" />
      <CardContent>
        <Timeline>
          {events ? (
            events
              .slice()
              .reverse()
              .map(event => {
                if (
                  event.type ===
                  OrderEventsEnum.CHANGE_REMITTANCE_MANUAL_ADJUSTMENT_STATUS
                ) {
                  return null;
                } else if (
                  event.type ===
                    OrderEventsEnum.CREATE_REMITTANCE_MANUAL_ADJUSTMENT &&
                  event.user !== null
                ) {
                  return (
                    <TimelineNoteRemittanceManualAdjust
                      event={event}
                      date={event.date}
                      user={event.user}
                      key={event.id}
                    />
                  );
                }

                if (
                  deliveryType !== DeliveryTypeEnum.IN_STORE_PICKUP &&
                  event.type === OrderEventsEnum.ORDER_MARKED_AS_COMPLETED &&
                  (deliveryImage?.url || dropOffImage?.url)
                ) {
                  return (
                    <TimelineImage
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      imageSrc={deliveryImage?.url || dropOffImage?.url}
                      key={event.id}
                    />
                  );
                } else if (
                  deliveryType !== DeliveryTypeEnum.IN_STORE_PICKUP &&
                  event.type === OrderEventsEnum.ORDER_MARKED_AS_SHIPPED &&
                  pickUpImage?.url
                ) {
                  return (
                    <TimelineImage
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      imageSrc={pickUpImage?.url}
                      key={event.id}
                    />
                  );
                } else if (
                  deliveryType !== DeliveryTypeEnum.IN_STORE_PICKUP &&
                  event.type ===
                    OrderEventsEnum.ORDER_PANDAGO_RETURNED_TO_VENDOR &&
                  returnImage?.url
                ) {
                  return (
                    <TimelineImage
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      imageSrc={returnImage?.url}
                      key={event.id}
                    />
                  );
                } else if (
                  eventsWithMessage.includes(event.type) &&
                  isNationwideOrderEvents(deliveryType, event.type)
                ) {
                  return (
                    <TimelineNoteNationwide
                      date={event.date}
                      user={event.user}
                      email={event?.user?.email}
                      message={getEventMessage(event, intl)}
                      key={event.id}
                    />
                  );
                } else if (
                  eventsWithMessage.includes(event.type) &&
                  event.user !== null &&
                  event.type !== OrderEventsEnum.ORDER_2GO_BOOKING_FAILED
                ) {
                  return (
                    <TimelineNote
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                    />
                  );
                } else if (
                  event.type === OrderEventsEnum.ORDER_2GO_BOOKING_FAILED
                ) {
                  return (
                    <TimelineNoteNationwide3plError
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                    />
                  );
                } else if (
                  event.type === OrderEventsEnum.ORDER_AUTOBOOKING_CANCELLED
                ) {
                  return (
                    <TimelineNoteNationwide3plError
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                      title={event.user.email}
                      titleTextColor="#000000"
                    />
                  );
                } else if (
                  event.type === OrderEventsEnum.ORDER_GRAB_BOOKING_CANCELED
                ) {
                  return (
                    <TimelineNoteNationwide3plError
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                      title={event.referenceId3pl}
                      titleTextColor="#000000"
                    />
                  );
                } else if (
                  event.type === OrderEventsEnum.ORDER_PANDAGO_BOOKING_CANCELED
                ) {
                  return (
                    <TimelineNoteNationwide3plError
                      date={event.date}
                      user={event.user}
                      message={event.message}
                      key={event.id}
                      title={event.referenceId3pl}
                      titleTextColor="#000000"
                    />
                  );
                } else if (
                  event.type === OrderEventsEnum.ORDER_AUTOBOOKING_FAILED
                ) {
                  return (
                    <TimelineNoteNationwide3plError
                      date={event.date}
                      user={""}
                      message={event.message}
                      key={event.id}
                      title={"Autobooking Failed"}
                      titleTextColor="#000000"
                    />
                  );
                }
                return (
                  <TimelineEvent
                    date={event.date}
                    title={getEventMessage(event, intl)}
                    key={event.id}
                  />
                );
              })
          ) : (
            <Skeleton />
          )}
        </Timeline>
      </CardContent>
    </Card>
  );
};
OrderHistory.displayName = "History";
export default OrderHistory;
